import { createSelector } from 'reselect';
import { CallbackSaga, createGeneralCall } from '@altermaind/core/requests-status-management';
import { IS_MOCK_ENABLED } from '@/_shared/constants/mock';
import {
  formatDateFromBEToLocalizied,
  getTimeElapsedSince,
} from '@/_shared/helpers/convertDateAndTimeToFormatForBackend';
import { GenericGet } from '@/_shared/types';
import { GenericApiError } from '@/_shared/types/apiError';
import { UserWarningType } from '@/swagger/constants/slash-enums';
import { API_ORGANIZATIONS_ORGANIZATIONID_USERS_WARNINGS_PATH_KEY } from '@/swagger/constants/slash-keys';
import { API_GET_USER_WARNINGS, KEY_GET_USER_WARNINGS } from '../constants';
import { MOCK_RESPONSE_GET_USER_WARNINGS } from '../mocks';
import { getCurrentOrganization } from '../selectors';

export type CallParamsGetUserWarnings = {
  successSaga?: CallbackSaga;
  errorSaga?: CallbackSaga;
};

type CallResponseGetUserWarnings = GenericGet<
  typeof API_ORGANIZATIONS_ORGANIZATIONID_USERS_WARNINGS_PATH_KEY
>['response']['body'];

type CallErrorGetUserWarnings = GenericApiError;

type TrialExpiringInfo = {
  type: 'info' | 'warning';
  expiringDate: string;
  hasShowDialog: boolean;
  deltaOfDays?: number;
};

type BetaExpiringInfo = {
  expiringDate: string;
};

type CallSelectorsGetUserWarnings = {
  isTrialExpiring: TrialExpiringInfo | undefined;
  isDialogTrialExpiringDismissed: boolean;
  isPaymentFailed: boolean;
  isBetaExpiring: BetaExpiringInfo | undefined;
  isSubscriptionMissing: boolean;
};

type CallReducersGetUserWarnings = {
  dismissDialogTrialExpiring: void;
};

export const getUserWarningsCall = createGeneralCall<
  CallParamsGetUserWarnings,
  CallResponseGetUserWarnings,
  CallErrorGetUserWarnings,
  CallSelectorsGetUserWarnings,
  unknown,
  CallReducersGetUserWarnings
>({
  id: KEY_GET_USER_WARNINGS,
  url: () => state => {
    const org = getCurrentOrganization(state);
    return API_GET_USER_WARNINGS(org as unknown as string);
  },
  method: 'get',
  paramsSelector:
    ({ payload }) =>
    () =>
      payload,
  successMock: IS_MOCK_ENABLED() ? MOCK_RESPONSE_GET_USER_WARNINGS : undefined,
  selectors: {
    isDialogTrialExpiringDismissed: ({ selectors }) =>
      createSelector(
        selectors.request,
        req =>
          (req as unknown as { isDialogTrialExpiringDismissed: boolean })
            ?.isDialogTrialExpiringDismissed ?? false,
      ),
    isPaymentFailed: ({ selectors }) =>
      createSelector(selectors.response, response => {
        if (!response || !Array.isArray(response)) {
          return false;
        }
        return response.some(warn => warn.type === UserWarningType.SUBSCRIPTION_PAYMENT_FAILED);
      }),
    isSubscriptionMissing: ({ selectors }) =>
      createSelector(selectors.response, response => {
        if (!response || !Array.isArray(response)) {
          return false;
        }
        return response.some(warn => warn.type === UserWarningType.SUBSCRIPTION_MISSING);
      }),
    isBetaExpiring: ({ selectors }) =>
      createSelector(selectors.response, response => {
        if (!response || !Array.isArray(response)) {
          return undefined;
        }
        const warning = response?.find(
          warn => warn.type === UserWarningType.SUBSCRIPTION_BETA_EXPIRING,
        );
        if (!warning?.metadata) {
          return undefined;
        }
        const expiringDate = (warning.metadata.expiringDate as unknown as string) || '';
        return {
          expiringDate: expiringDate ? formatDateFromBEToLocalizied(expiringDate) : '',
        };
      }),
    isTrialExpiring: ({ selectors }) =>
      createSelector(selectors.response, response => {
        if (!response || !Array.isArray(response)) {
          return undefined;
        }
        const warning = response?.find(
          warn => warn.type === UserWarningType.SUBSCRIPTION_TRIAL_EXPIRING,
        );
        if (!warning?.metadata) {
          return undefined;
        }

        const level = (warning.metadata.level as unknown as TrialExpiringInfo['type']) || 'info';
        const expiringDate = (warning.metadata.expiringDate as unknown as string) || '';
        const hasShowDialog = (warning.metadata.showDialog as unknown as boolean) || false;

        return {
          type: level,
          expiringDate: expiringDate ? formatDateFromBEToLocalizied(expiringDate) : '',
          hasShowDialog,
          ...(level === 'warning' && expiringDate
            ? { deltaOfDays: getTimeElapsedSince(expiringDate, 'days') }
            : {}),
        };
      }),
  },
  reducers: {
    dismissDialogTrialExpiring: () => state => ({ ...state, isDialogTrialExpiringDismissed: true }),
  },
});
